/**
 * This API is the high level API for storing information regarding tasks and current position
 * for lessons.
 *
 * This should be a account-only event, which means we should make sure we're logged in and
 * everything first.
 */

import { createFirebaseCache } from "@utils/localStorageFirebase"
import { userSecurityToken, userStatus } from "@utils/underhood/firebase"
import { derived, get, type Readable, readable, writable } from "svelte/store"
import { currentDate, Day, type DaysSinceBeginning, type MinutesAfterMidnight } from "./day-time"
import { ReadingList, type ReadingListStorage } from "./reading-list"
import { without, union } from "lodash-es"
import { addDays, addMinutes, differenceInMinutes, startOfDay } from "date-fns"
import { checkAndOpenPracticeLessonConflict } from "../../routes/practice/ConflictLessonPopup.svelte"
import { deviceIDNative } from "@utils/underhood/device"
import {
    notificationPermissions,
    NotificationPermissions,
} from "@utils/cap-notification-manager/notification-permission"
import {
    clearAllNotifications,
    currentNativeNotifications,
} from "@utils/cap-notification-manager/cap-notification-manager"
import { asyncDerived } from "@utils/helpers/async-derived"

/**
 * This should store the lesson on any particular device
 */
export type DeviceData = {
    lesson: number
}

export type DeviceDataList = {
    [device: string]: DeviceData
}

/**
 * This is buffer applied outside the wake and sleep range
 * where you're allowed to drag your reminders
 */
const SLEEP_BUFFER: MinutesAfterMidnight = 90

/**
 * Firebase data
 */
type PracticeRegistrationData = {
    wake?: MinutesAfterMidnight
    sleep?: MinutesAfterMidnight
    readingList?: ReadingListStorage
    currentLesson?: number
    currentLessonStart?: DaysSinceBeginning
    devices?: DeviceDataList
    /**
     * Legacy but needed for backwards compatibility
     */
    preselects?: object
}

export class LiveNotificationData {
    readonly today: Day
    constructor(
        readonly parent: NotificationData,
        readonly date: Date,
    ) {
        this.today = Day.fromDay(date)
        // console.log("New live log: ", {
        //     deltaStartDay: this.deltaStartDay,
        //     lessonHasPassed: this.lessonHasPassed,
        //     currentLesson: this.parent.currentLesson,
        //     currentLessonStartday: this.parent.currentLessonStartDay.daySinceBeginning,
        //     actuallyToday: Day.fromToday().daySinceBeginning,
        // })
    }

    /**
     * How many days until today since the currently selected lesson.
     * In other words, how many days have
     * we lagged behind (1 = the current lesson passed yesterday)
     * @param today
     * @returns
     */
    get deltaStartDay() {
        return this.parent.currentLessonStartDay.differenceBetween(this.today)
    }

    /**
     * Is `today` ahead of our currently selected lesson?
     * @param today
     * @returns
     */
    get lessonHasPassed() {
        return this.deltaStartDay >= 1
    }

    /**
     * What lesson should we be on ? Takes into account epilogue
     * @param today
     * @returns null if no lesson or epilogue, the lesson if it is
     */
    get hypotheticalCurrentLesson() {
        // If we're not pending, then it's null
        if (!this.lessonHasPassed) {
            console.log(`lesson has passed, delta start day ${this.deltaStartDay}`)
            return null
        }
        // If epilogue, return null
        if (this.parent.currentLesson >= FINAL_LESSON_NUMBER) {
            console.log(
                `current lesson greater than final lesson number: ${this.parent.currentLesson}`,
            )
            return null
        }
        return this.parent.currentLesson + 1
    }

    /**
     * Whether it's too early to be doing the lessons
     */
    get isTooEarly() {
        const start = startOfDay(Date.now())
        const minutesFromStartOfDay = differenceInMinutes(this.date, start)

        return minutesFromStartOfDay < this.parent.bufferedWake
    }

    gotoNextLesson() {
        const nextLesson = this.hypotheticalCurrentLesson
        if (nextLesson === null)
            throw new Error(
                `nextLesson was null, delta start day : ${this.deltaStartDay}, lesson: ${this.parent.currentLesson}`,
            )

        // Reset lesson
        return this.parent.resetLesson(this.today, nextLesson)
    }
}

class NotificationData {
    readonly wake: MinutesAfterMidnight
    readonly sleep: MinutesAfterMidnight
    readonly readingList: ReadingList
    readonly #readingListStorage: ReadingListStorage
    readonly currentLesson: number
    readonly currentLessonStart: DaysSinceBeginning
    readonly devices: DeviceDataList

    readonly live: Readable<LiveNotificationData>

    constructor(practiceData: PracticeRegistrationData) {
        if (practiceData.wake === undefined) throw new Error(`Wake was undefined`)
        if (practiceData.sleep === undefined) throw new Error(`Sleep was undefined`)
        if (practiceData.readingList === undefined) throw new Error(`Reading list was undefined`)
        if (practiceData.currentLesson === undefined) throw new Error(`Current lesson undefined`)
        if (practiceData.currentLessonStart === undefined)
            throw new Error(`Current lesson start undefined`)
        if (practiceData.devices === undefined) throw new Error(`Devices undefined`)
        this.wake = practiceData.wake
        this.sleep = practiceData.sleep
        this.#readingListStorage = structuredClone(practiceData.readingList)
        this.readingList = new ReadingList(this.#readingListStorage)
        this.currentLesson = practiceData.currentLesson
        this.currentLessonStart = practiceData.currentLessonStart
        this.devices = structuredClone(practiceData.devices)

        console.log("New notification data: ", {
            currentLesson: this.currentLesson,
            currentLessonStart: this.currentLessonStart,
        })

        // TODO this should be unsubscribed but it will only ever cause a memory leak
        // if the user logs in and out a million times so it shouldn't be an issue
        // until migration to Svelte 5
        this.live = derived(currentDate, (todate) => {
            return new LiveNotificationData(this, todate)
        })
    }

    /**
     * Some time less than wake up time
     */
    get bufferedWake(): MinutesAfterMidnight {
        return Math.max(this.wake - SLEEP_BUFFER, 0)
    }

    /**
     * Some time more than sleep time
     */
    get bufferedSleep(): MinutesAfterMidnight {
        const midnight = 60 * 24
        return Math.min(midnight, this.sleep + SLEEP_BUFFER)
    }

    get currentLessonStartDay() {
        return new Day(this.currentLessonStart)
    }

    resetReadingList() {
        cache.setMerge({ readingList: {} })
    }

    /**
     * Updates the schedule but also cancels native notifications.
     * Make sure to clear all notifications first!
     * @param wake
     * @param sleep
     * @returns
     * @throws if wake > sleep
     */
    updateSchedule(wake: number, sleep: number) {
        clearAllNotifications()
        if (sleep < wake) throw new Error("Wake up time is less than sleep time!")
        return cache.setMerge({
            wake,
            sleep,
        })
    }

    async resetReadingAndMoveToLesson(lesson = 1) {
        if (lesson < 0 || lesson > FINAL_LESSON_NUMBER)
            throw new Error(`Lesson was not between 0 and 366: ${lesson}`)
        await this.resetReadingList()
        return this.resetLesson(Day.fromToday(), lesson)
    }

    /**
     *
     * @param day
     * @param lessonNumber
     * @returns
     * @throws if lesson is out of range
     */
    async resetLesson(day: Day, lessonNumber: number) {
        {
            // Remove notifications from native reminder scheduler
            if (get(currentNativeNotifications)) clearAllNotifications()

            if (lessonNumber > FINAL_LESSON_NUMBER || lessonNumber < 0) {
                throw new Error(
                    `Lesson was out of range, either greater than ${FINAL_LESSON_NUMBER} or less than 0`,
                )
            }

            // Add the device ID one so it doesn't conflict, but only if permission granted
            const deviceID = await deviceIDNative

            const deviceStatus = {
                lesson: lessonNumber,
            }

            const add =
                get(notificationPermissions) === NotificationPermissions.GRANTED
                    ? {
                          devices: {
                              [deviceID]: deviceStatus,
                          },
                      }
                    : {}

            return cache.setMerge({
                currentLessonStart: day.daySinceBeginning,
                currentLesson: lessonNumber,
                ...add,
            })
        }
    }

    /**
     * Keeps the same lesson but resets the start day
     * so it matches with today.
     *
     * This is the "stay on the current lesson" option
     *
     * Also removes notifications
     */
    resetDay() {
        return this.resetLesson(Day.fromToday(), this.currentLesson)
    }

    gotoLessonToday(lesson: number) {
        return this.resetLesson(Day.fromToday(), lesson)
    }

    get currentLessonItem() {
        return this.getItem(this.currentLesson)
    }

    undoReadingItem(lesson: number, item: number | string) {
        // We need to take the whole readingList because of Firebase's way of
        // merging - only one layer deep
        const readingListStorage = structuredClone(this.#readingListStorage)
        readingListStorage[lesson] = without(readingListStorage[lesson] ?? [], item)

        return cache.setMerge({
            readingList: readingListStorage,
        })
    }

    setReadingItem(lesson: number, item: number | string) {
        const readingList = structuredClone(this.#readingListStorage)
        readingList[lesson] = union(readingList[lesson] ?? [], [item])

        return cache.setMerge({
            readingList,
        })
    }

    async syncThisDeviceLesson() {
        const token = userSecurityToken()
        // Add the device ID one so it doesn't conflict, but only if permission granted
        const deviceID = await deviceIDNative
        const add =
            get(notificationPermissions) === NotificationPermissions.GRANTED
                ? {
                      devices: {
                          [deviceID]: {
                              lesson: this.currentLesson,
                          },
                      },
                  }
                : {}

        // Make sure we haven't changed users
        if (!token()) return
        return cache.setMerge(add)
    }

    getItem(lesson: number) {
        const item = this.readingList.getLessonItem(lesson)
        if (item === null) throw new Error(`Getting lesson ${lesson} item was null`)
        return item
    }
}

export type NotificationDataType = NotificationData

const FINAL_LESSON_NUMBER = 366 // To account  for epilogue

/**
 * This lets the main loader know that notifications are ready or not
 * and we should show the app. Note this will return true even if not
 * connected or registered
 */
export const notificationsDataReady = writable(false)

/**
 * This tells us whether we are connected to an account,
 * in effect either registered or ready to register.
 */
export const notificationsDataConnected = readable(false, (set) => {
    userStatus.subscribe((status) => set(status === "verified"))
})

const cache = createFirebaseCache<PracticeRegistrationData>({
    name: "lessonprogress",
    version: 1,
    def: {},
    noLocal: true,
    ready: notificationsDataReady,
})

function checkIfRegistered(registration: PracticeRegistrationData) {
    return (
        registration?.currentLesson !== undefined &&
        registration.currentLessonStart !== undefined &&
        registration.sleep !== undefined &&
        registration.wake !== undefined &&
        registration.readingList !== undefined
    )
}

/**
 * null if unregistered or if loading
 */
export const notificationData = asyncDerived<
    NotificationData | null,
    PracticeRegistrationData | null
>(
    cache,
    async (data, current) => {
        if (data === null) return null
        if (!checkIfRegistered(data)) return null
        console.log("new notification data", { data })
        const newData = new NotificationData(data)
        return newData
    },
    null,
)

/**
 * This function should be called to register your preferences
 * at the beginning of the setup
 */
export async function registerSetup({
    wake,
    sleep,
    lesson,
    startTomorrow,
}: {
    wake: MinutesAfterMidnight
    sleep: MinutesAfterMidnight
    lesson: number
    startTomorrow: boolean
}) {
    const startDay = startTomorrow ? Day.fromToday().add(-1) : Day.fromToday()
    setRegistration({
        wake,
        sleep,
        currentLesson: lesson,
        currentLessonStart: startDay.daySinceBeginning,
        readingList: {},
        devices: {},
        /**
         * Legacy but needed for backwards compatibility
         */
        preselects: {},
    })
}

export const resetLessons = () => setRegistration({})

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
globalThis.resetLessons = () => setRegistration({})

/**
 * Sets the registration data on the server. This should be called only
 * if you're bypassing the {@link registerSetup} function.
 * @param data
 * @returns
 */
async function setRegistration(data: PracticeRegistrationData) {
    if (get(notificationsDataReady) === false)
        throw new Error("Attempted to set registration but notifications was not ready")
    if (get(notificationsDataConnected) === false)
        throw new Error("Attempted to set registration but notifications was not connected")

    // Just in case
    if (get(userStatus) !== "verified")
        throw new Error(
            "Notification data was connected but user status was not verified - this should never happen as notification data connected should depend on user verification status.",
        )

    return await cache.set(data)
}

/**
 * Formats the date in such a way that the next lesson
 * is never displayed more than a day ahead.
 * @param lesson the lesson we want to get the info for
 * @param currentLesson the lesson we're currently on
 * @param currentDay today's date
 * @param deltaDays the difference between the day we did the last lesson and today (0 = same lesson as today, no pending to next lesson)
 * @returns
 */
export const dateDisplayer = (
    lesson: number,
    currentLesson: number,
    currentDay: Date,
    deltaDays: number,
) => {
    const done = lesson < currentLesson
    const futureLesson = lesson > currentLesson
    if (futureLesson) {
        // Date of future lesson should be no more than a day ahead
        const hypotheticalNextLesson = currentLesson + Math.min(deltaDays, 1)
        const differenceFromHypotheticalNextLesson = lesson - hypotheticalNextLesson
        return {
            done,
            date: addDays(currentDay, differenceFromHypotheticalNextLesson),
        }
    } else {
        return {
            done,
            date: addDays(currentDay, lesson - currentLesson - deltaDays),
        }
    }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
globalThis.addDays = (days: number) => {
    const notfData = get(notificationData)
    if (!notfData) {
        console.log("Notf data not set")
        return
    }
    notfData.resetLesson(notfData.currentLessonStartDay?.add(days), notfData.currentLesson)
}
